import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { THEME_LABELS, LIGHT_THEMES, DARK_THEMES, THEME_CONFIGS } from '../../../config/themes';
import { LANGUAGE_LABELS, AVAILABLE_LANGUAGES } from '../../../config/translations';
import { ThemeName } from '../../../types/theme';
import { authUserSignal } from '../../../signals/authSignal';
import { adminService } from '../../../services';
import { createLogger } from '../../../utils/logger';

const logger = createLogger('[SettingsPanel]');

interface SettingsPanelProps {
  onClose: () => void;
}

export default function SettingsPanel({ onClose }: SettingsPanelProps) {
  const { setLightTheme, setDarkTheme, currentTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  
  // Determine initial active theme tab based on current theme's mode
  const currentThemeMode = THEME_CONFIGS[currentTheme]?.mode || 'light';
  const [activeThemeTab, setActiveThemeTab] = useState<'light' | 'dark'>(currentThemeMode as 'light' | 'dark');

  // Admin section state
  const [sortLoading, setSortLoading] = useState(false);
  const [sortMessage, setSortMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isAdmin = authUserSignal.value?.role === 'admin';

  const handleSortPhotos = async () => {
    setSortLoading(true);
    setSortMessage(null);
    try {
      const result = await adminService.sortPhotos();
      logger.info(`Photos sorted: ${result.count} photos randomized`);
      setSortMessage({ type: 'success', text: `✓ ${result.count} photos sorted` });
      setTimeout(() => setSortMessage(null), 3000);
    } catch (error) {
      logger.error('Failed to sort photos:', error);
      setSortMessage({ type: 'error', text: 'Failed to sort photos' });
      setTimeout(() => setSortMessage(null), 3000);
    } finally {
      setSortLoading(false);
    }
  };

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Sync active tab when current theme changes
  useEffect(() => {
    const themeMode = THEME_CONFIGS[currentTheme]?.mode || 'light';
    setActiveThemeTab(themeMode as 'light' | 'dark');
  }, [currentTheme]);

  // Get accent color for theme swatch
  const getThemeColor = (theme: ThemeName): string => {
    const themeConfig = THEME_CONFIGS[theme];
    return themeConfig?.colors?.primary || '#0066FF';
  };

  return (
    <div className="settings-modal-overlay" onClick={onClose}>
      <div
        className="settings-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="settings-modal-header">
          <h2 className="settings-modal-title">{t('settings.title')}</h2>
          <button
            onClick={onClose}
            className="settings-modal-close-btn"
            aria-label="Close settings"
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Language Section */}
        <div className="settings-section">
          <h3 className="settings-section-title">{t('settings.language')}</h3>
          <div className="settings-theme-grid">
            {AVAILABLE_LANGUAGES.map(lang => (
              <button
                key={lang}
                onClick={() => setLanguage(lang)}
                className={`settings-theme-btn ${language === lang ? 'active' : ''}`}
              >
                <div className="settings-theme-swatch" style={{ backgroundColor: '#6c757d' }} />
                {LANGUAGE_LABELS[lang]}
              </button>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="settings-divider" />

        {/* Theme Section with Tabs */}
        <div className="settings-section">
          <h3 className="settings-section-title">{t('settings.theme')}</h3>
          
          {/* Theme Tabs */}
          <div className="settings-theme-tabs">
            <button
              className={`settings-theme-tab-btn ${activeThemeTab === 'light' ? 'active' : ''}`}
              onClick={() => setActiveThemeTab('light')}
            >
              {t('settings.light')}
            </button>
            <button
              className={`settings-theme-tab-btn ${activeThemeTab === 'dark' ? 'active' : ''}`}
              onClick={() => setActiveThemeTab('dark')}
            >
              {t('settings.dark')}
            </button>
          </div>

          {/* Theme Grid - shows Light or Dark themes based on active tab */}
          <div className="settings-theme-grid">
            {(activeThemeTab === 'light' ? LIGHT_THEMES : DARK_THEMES).map(theme => (
              <button
                key={theme}
                onClick={() => {
                  if (activeThemeTab === 'light') {
                    setLightTheme(theme);
                  } else {
                    setDarkTheme(theme);
                  }
                }}
                className={`settings-theme-btn ${currentTheme === theme ? 'active' : ''}`}
              >
                <div
                  className="settings-theme-swatch"
                  style={{ backgroundColor: getThemeColor(theme) }}
                />
                {THEME_LABELS[theme]}
              </button>
            ))}
          </div>
        </div>

        {/* Admin Section - only visible to admins */}
        {isAdmin && (
          <>
            <div className="settings-divider" />
            <div className="settings-section">
              <h3 className="settings-section-title">Admin</h3>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                Randomize photo display order
              </p>
              <button
                onClick={handleSortPhotos}
                disabled={sortLoading}
                className="settings-admin-btn"
                style={{ opacity: sortLoading ? 0.6 : 1, cursor: sortLoading ? 'not-allowed' : 'pointer' }}
              >
                {sortLoading ? 'Sorting...' : 'Sort Photos'}
              </button>
              {sortMessage && (
                <div
                  style={{
                    marginTop: '10px',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    backgroundColor: sortMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                    color: sortMessage.type === 'success' ? '#155724' : '#721c24',
                  }}
                >
                  {sortMessage.text}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
